import {
  Box,
  Flex,
  HelpText,
  Link,
  Table,
  Tag,
  Text,
  Select,
  SrOnly,
  Popover,
  Textarea,
  Button,
  type TableColumn,
  useToasts,
  createForm,
  useId,
} from '@applyboard/crystal-ui';
import {
  CheckMarkOutlineIcon,
  ClockOutlineIcon,
  CloseOutlineIcon,
  DeleteOutlineIcon,
  DocumentCheckOutlineIcon,
  EditOutlineIcon,
  HourglassHalfOutlineIcon,
  ErrorOutlineIcon,
  QuestionOutlineIcon,
  ChevronDownOutlineIcon,
  ChevronUpOutlineIcon,
} from '@applyboard/ui-icons';
import { useTranslation } from '@applyboard/ui-utils';
import { TFunction } from 'i18next';
import {
  ReadableValue,
  canAnswerQuestions,
  canUploadFiles,
  isEmptyValue,
  isFieldComponent,
  type FormDefinition,
} from '~/components/DynamicForm';
import {
  type AnswersHistory,
  ApplicationRequirement,
  Check,
  Decision,
  type UserAttachment,
} from '~/types';
import { canUpdateReqAttribute } from '~/utils/actions';
import { generateFormFileInformation } from '../RequirementList.utils';
import { assert } from '~/utils/assert';
import { match } from 'ts-pattern';
import { createContext, useMemo, useState } from 'react';
import { useFeatureFlags, useUpdateApplicationRequirement } from '../../../data';
import { useContextOrThrow } from '~/utils/hooks';
import { upsert } from '~/utils/upsert';
import { convertArrayToMap } from '~/utils/convertArrayToMap';
import { useRequirementListEventTracker } from '../../../utils/eventTracking';
import { useTheme } from '@emotion/react';
import { useEvaluationMetadata } from '../../../data/evaluationMetadata';

const RequirementEvaluationTableContext = createContext<
  | (ReturnType<typeof useSubmitEvaluation> & {
      showMessageColumn: boolean;
      requirement: ApplicationRequirement;
      canEvaluate: boolean;
      showInvalidState: boolean;
    })
  | null
>(null);

export function RequirementEvaluationTable({
  requirement,
  index,
  'aria-labelledby': ariaLabelledBy,
  disallowEvaluation,
  showInvalidState = false,
  onDeleteAttachment,
  onPendingEvaluationChange,
}: {
  requirement: ApplicationRequirement;
  index: number;
  'aria-labelledby'?: string;
  disallowEvaluation?: boolean;
  showInvalidState?: boolean;
  onDeleteAttachment?: (attachmentId: string) => void;
  onPendingEvaluationChange?: (isPending: boolean) => void;
}) {
  const { t } = useTranslation('RequirementEvaluationTable');
  const { featureFlags } = useFeatureFlags();
  const theme = useTheme();

  const data = useMemo(() => getTableData(requirement, index, t), [requirement, index, t]);

  const showMessageColumn = data.some(
    item => item && 'reasonAdditionalNote' in item && !!item.reasonAdditionalNote,
  );

  const hasPermissionToEvaluate = canUpdateReqAttribute(requirement, 'answers.decisions');

  const canEvaluate =
    !disallowEvaluation && hasPermissionToEvaluate && requirement.status === 'reviewing';
  const hasAttachments = canUploadFiles(requirement.formDefinition);
  const hasChecks =
    featureFlags['application_apr_checks'] &&
    data.some(item => 'checks' in item && item.checks.length > 0);

  const columns = useMemo(
    (): TableColumn<(typeof data)[number]>[] => [
      {
        accessorKey: 'answer',
        header:
          hasAttachments && !canAnswerQuestions(requirement.formDefinition)
            ? t('documentHeader')
            : t('answerHeader'),
        cell: (props: (typeof data)[number]) => (
          // TODO: Ensure this red line spans height of entire row
          <div
            css={theme => ({
              borderLeft:
                'evaluation' in props && props.evaluation === 'rejected'
                  ? `4px solid ${theme.colors.iconNegative}`
                  : 'none',
            })}
          >
            <Table.TextCell variant={'question' in props ? 'labelL' : 'bodyM'}>
              {'question' in props ? (
                props.question
              ) : (
                <div
                  css={{
                    width: 'max-content',
                    maxWidth: '360px',
                    '@media (max-width: 540px)': {
                      maxWidth: '240px',
                    },
                    marginLeft:
                      'evaluation' in props && props.evaluation === 'rejected' ? '-4px' : undefined,
                    paddingLeft: theme.spacing[4],
                  }}
                >
                  {props.answer}
                </div>
              )}
            </Table.TextCell>
          </div>
        ),
        width: 'content',
        cellSpansEntireTable: props => 'question' in props,
      },
      ...(hasChecks
        ? [
            {
              accessorKey: 'checks',
              header: t('evaluationChecksHeader'),
              width: 'content' as const,
              cell: (props: (typeof data)[number]) => {
                assert('checks' in props, 'Checks is not defined');

                return (
                  <Table.Cell>
                    <div style={{ width: '305px' }}>
                      <Flex direction="column">
                        {props.checks.map((check, idx) => (
                          <CheckResults key={idx} check={check} />
                        ))}
                      </Flex>
                    </div>
                  </Table.Cell>
                );
              },
            },
          ]
        : []),
      {
        accessorKey: 'evaluation',
        header: t('evaluationHeader'),
        width: 'content',
        cell: (props: (typeof data)[number]) => {
          assert('evaluation' in props, 'Evaluation is not defined');

          if (canEvaluate) {
            return (
              <EvaluationSelect
                {...props}
                onPendingEvaluationChange={onPendingEvaluationChange}
                requirementTemplateNumber={requirement.requirementTemplateNumber}
              />
            );
          }

          return <EvaluationIndicator {...props} />;
        },
      },
      {
        accessorKey: 'reasonAdditionalNote',
        header: () => <MessageToApplicantHeader />,
        cell: (props: (typeof data)[number]) => {
          if (!('reasonAdditionalNote' in props)) {
            return null;
          }

          return (
            <MessageToApplicant {...props} onPendingEvaluationChange={onPendingEvaluationChange} />
          );
        },
      },
      ...(hasAttachments && onDeleteAttachment
        ? [
            {
              accessorKey: 'deleteAttachmentId',
              header: () => <SrOnly>{t('removeDocument')}</SrOnly>,
              cell: (props: (typeof data)[number]) =>
                'deleteAttachmentId' in props && props.deleteAttachmentId ? (
                  <td>
                    <Box pr={2}>
                      <Button
                        leadIcon={DeleteOutlineIcon}
                        aria-label={t('removeDocument')}
                        emphasis="transparent"
                        intent="primary"
                        size="sm"
                        onClick={() => {
                          if ('deleteAttachmentId' in props && props.deleteAttachmentId) {
                            onDeleteAttachment(props.deleteAttachmentId);
                          }
                        }}
                      />
                    </Box>
                  </td>
                ) : (
                  <td></td>
                ),
              width: 'content' as const,
            },
          ]
        : []),
    ],
    [
      hasChecks,
      hasAttachments,
      requirement.formDefinition,
      t,
      onDeleteAttachment,
      theme.spacing,
      canEvaluate,
      onPendingEvaluationChange,
      requirement.requirementTemplateNumber,
    ],
  );

  return (
    <RequirementEvaluationTableContext.Provider
      value={{
        ...useSubmitEvaluation(requirement),
        requirement,
        showMessageColumn,
        canEvaluate,
        showInvalidState,
      }}
    >
      <Flex direction="column" gap={4}>
        <Table aria-labelledby={ariaLabelledBy} overflow="scroll" columns={columns} data={data} />
      </Flex>
    </RequirementEvaluationTableContext.Provider>
  );
}

function CheckResults({ check }: { check: Check }) {
  const { t } = useTranslation('RequirementEvaluationTable');
  const { evaluationMetadata } = useEvaluationMetadata();
  const checkDefinition = evaluationMetadata.checkDefinitions.find(
    def => def.code === check.checkCode,
  );
  assert(checkDefinition, 'Check definition not found');

  const resultDefinition = checkDefinition.results.find(result => result.code === check.resultCode);
  assert(resultDefinition, 'Result definition not found');

  const [expanded, setExpanded] = useState(false);
  const detailsId = useId();

  return (
    <Flex direction="column">
      <Flex gap={2} align="center">
        {match(resultDefinition.code)
          .with('positive', () => <CheckMarkOutlineIcon intent="positive" />)
          .with('negative', () => <ErrorOutlineIcon intent="negative" />)
          .with('warning', () => <QuestionOutlineIcon intent="warning" />)
          .exhaustive()}
        <Text intent={resultDefinition.code} overflow="nowrap">
          {resultDefinition.displayName}
        </Text>
        {check.resultDescription ? (
          <Button
            leadIcon={expanded ? ChevronUpOutlineIcon : ChevronDownOutlineIcon}
            aria-label={t('expandCheckButtonAriaLabel')}
            emphasis="transparent"
            size="sm"
            onClick={() => setExpanded(!expanded)}
            aria-expanded={expanded}
            aria-controls={detailsId}
          />
        ) : (
          <div style={{ height: '40px' }}></div>
        )}
      </Flex>
      <div id={detailsId}>
        {expanded && (
          <Box pb={2} pl={8}>
            <Text variant="bodyS" intent={resultDefinition.code}>
              {check.resultDescription}
            </Text>
          </Box>
        )}
      </div>
    </Flex>
  );
}

function MessageToApplicant({
  answerKey,
  reason,
  reasonAdditionalNote,
  evaluation,
  item,
  onPendingEvaluationChange,
}: Extract<TableData, { answerKey: string }> & {
  onPendingEvaluationChange?: (isPending: boolean) => void;
}) {
  const toasts = useToasts();
  const { trackEvent } = useRequirementListEventTracker();
  const [open, setOpen] = useState(false);

  const { canEvaluate, submitEvaluation, getPendingSubmission } = useContextOrThrow(
    RequirementEvaluationTableContext,
  );
  const pendingSubmission = getPendingSubmission(answerKey, item);
  const disabled = Boolean(pendingSubmission);
  const loading =
    pendingSubmission?.status === 'rejected' &&
    (pendingSubmission.reason !== reason ||
      pendingSubmission.reasonAdditionalNote !== reasonAdditionalNote);
  const pendingReasonAdditionalNote =
    evaluation === 'rejected' ? pendingSubmission?.reasonAdditionalNote : undefined;

  const { t } = useTranslation('RequirementEvaluationTable');
  const messageToApplicant = <Text>{pendingReasonAdditionalNote || reasonAdditionalNote}</Text>;
  const dismissEventText = 'Review - Edit message to applicant - Dismissed edit evaluation';

  return (
    <Table.TextCell>
      <div
        css={{
          width: 'max-content',
          maxWidth: '360px',
          '@media (max-width: 540px)': {
            maxWidth: '240px',
          },
        }}
      >
        {canEvaluate && reasonAdditionalNote ? (
          <Popover
            open={open}
            onOpenChange={newOpen => {
              if (!newOpen) {
                trackEvent({
                  event: dismissEventText,
                });
              }
              setOpen(newOpen);
            }}
            placement="bottom-end"
          >
            <Flex gap={2} align="center">
              {messageToApplicant}
              <Flex.Item shrink={0}>
                <Popover.Trigger>
                  <Button
                    leadIcon={EditOutlineIcon}
                    aria-label={t('updatePopover.heading')}
                    emphasis="transparent"
                    intent="primary"
                    size="sm"
                    loading={loading}
                    disabled={disabled}
                    onClick={() =>
                      trackEvent({
                        event: 'Review - Clicked edit message to applicant',
                      })
                    }
                  />
                </Popover.Trigger>
              </Flex.Item>
            </Flex>
            <Popover.Content noShortDescription>
              <Form
                defaultValues={{
                  reason,
                  reasonAdditionalNote,
                }}
                onSubmit={values => {
                  onPendingEvaluationChange?.(true);
                  submitEvaluation({
                    answerKey,
                    item,
                    status: 'rejected',
                    reason: values.reason,
                    reasonAdditionalNote: values.reasonAdditionalNote,
                    onError: toasts.negative,
                    onSuccess: () => onPendingEvaluationChange?.(false),
                  });
                  setOpen(false);
                  trackEvent({
                    event: 'Review - Edit message to applicant - Clicked update evaluation',
                    data: {
                      failCategory: values.reason,
                      messageToApplicant: values.reasonAdditionalNote,
                    },
                  });
                }}
              >
                <Popover.Heading>{t('updatePopover.heading')}</Popover.Heading>
                <Flex direction="column" gap={4}>
                  <EvaluationFormFields />
                </Flex>
                <Popover.Actions>
                  <Button
                    emphasis="highlighted"
                    width="fill"
                    size="sm"
                    onClick={() => {
                      setOpen(false);
                      trackEvent({
                        event: dismissEventText,
                      });
                    }}
                  >
                    {t('updatePopover.cancelButton')}
                  </Button>
                  <Button intent="primary" width="fill" type="submit" size="sm">
                    {t('updatePopover.confirmButton')}
                  </Button>
                </Popover.Actions>
              </Form>
            </Popover.Content>
          </Popover>
        ) : (
          messageToApplicant
        )}
      </div>
    </Table.TextCell>
  );
}

function MessageToApplicantHeader() {
  const { t } = useTranslation('RequirementEvaluationTable');
  const { showMessageColumn } = useContextOrThrow(RequirementEvaluationTableContext);

  return <div style={{ whiteSpace: 'nowrap' }}>{showMessageColumn ? t('messageHeader') : ''}</div>;
}

function formatAttachmentLink(
  attachments: UserAttachment[] | undefined,
  value: string | undefined,
) {
  if (!attachments) return null;
  const attachment = attachments.find(attachment => attachment.id === value);
  const attachmentLink = attachment && (
    <Flex gap={2}>
      <DocumentCheckOutlineIcon intent="primary" size="md" />
      <Link href={attachment.downloadLink} mode="override_user_choice_and_open_in_new_tab">
        {attachment.name}
      </Link>
    </Flex>
  );
  return attachmentLink;
}

function getQuestionsInOrder(
  components: FormDefinition['components'],
  answers: AnswersHistory['answers'],
) {
  const answersMap = convertArrayToMap(answers, 'key');

  return components
    .filter(isFieldComponent)
    .map(component => answersMap[component.key])
    .filter(Boolean);
}

type TableData =
  | {
      question: string;
    }
  | {
      answerKey: string;
      item?: string;
      answer: React.ReactNode;
      checks: Check[];
      evaluation: 'approved' | 'rejected' | 'no_decision';
      reason: string;
      reasonAdditionalNote: string;
      deleteAttachmentId?: string;
    };

function getTableData(
  requirement: ApplicationRequirement,
  index: number,
  t: TFunction<'RequirementEvaluationTable'>,
): TableData[] {
  if (!requirement.responses?.answersHistory?.length) {
    return [];
  }

  const questionsInOrder = getQuestionsInOrder(
    requirement.formDefinition.components,
    requirement.responses.answersHistory[index].answers,
  );
  const formFileInformation = generateFormFileInformation(requirement);

  return questionsInOrder
    .filter(answer => !isEmptyValue(answer.value))
    .flatMap((answer, idx): TableData[] => {
      const questionDefinition = requirement.formDefinition.components.find(
        component => component.key === answer.key,
      );
      assert(
        questionDefinition && isFieldComponent(questionDefinition),
        'Question definition not found',
      );

      const question = {
        question: `${idx + 1}. ${questionDefinition.label}`,
      };

      if (questionDefinition.type === 'file') {
        assert(Array.isArray(answer.value), 'Answer value is not an array');

        return [
          question,
          ...answer.value.map(item => {
            const decision = answer.decisions.find(d => d.type === 'per_item' && d.item === item);
            const attachment = requirement.responses?.attachments?.find(
              attachment => attachment.id === item,
            );
            const hasDeletedAttachments = attachment?.deletionReason === 'pii';

            return {
              answerKey: answer.key,
              item,
              answer: requirement?.responses ? (
                <Flex gap={2} direction={{ xs: 'column', md: 'row' }} wrap={true}>
                  {hasDeletedAttachments ? (
                    <HelpText showIcon intent="warning">
                      {t('documentRemovedDueToPII')}
                    </HelpText>
                  ) : (
                    <>{formatAttachmentLink(requirement.responses.attachments, item)}</>
                  )}
                  <AttachmentTags questionDefinition={questionDefinition} />
                </Flex>
              ) : (
                item
              ),
              checks:
                answer.checks?.filter(check => check.type === 'per_item' && check.item === item) ||
                [],
              evaluation: decision?.status || ('no_decision' as const),
              reason: decision?.reason || '',
              reasonAdditionalNote: decision?.reasonAdditionalNote || '',
              deleteAttachmentId: !hasDeletedAttachments ? attachment?.id : undefined,
            };
          }),
        ];
      }

      const decision = answer.decisions.find(d => d.type === 'overall');
      return [
        question,
        {
          answerKey: answer.key,
          answer: [
            <ReadableValue
              component={questionDefinition}
              formFileInformation={formFileInformation}
              key={idx}
              value={answer.value}
            />,
          ],
          checks: [],
          evaluation: decision?.status || 'no_decision',
          reason: decision?.reason || '',
          reasonAdditionalNote: decision?.reasonAdditionalNote || '',
        },
      ];
    });
}

function EvaluationIndicator({ evaluation }: Extract<TableData, { answerKey: string }>) {
  const { requirement } = useContextOrThrow(RequirementEvaluationTableContext);
  const { t } = useTranslation('RequirementEvaluationTable');
  return (
    <Table.Cell>
      <Flex gap={2}>
        {match(evaluation)
          .with('approved', () => (
            <>
              <CheckMarkOutlineIcon intent="positive" />
              <Text intent="positive" overflow="nowrap">
                {t('evaluationPass')}
              </Text>
            </>
          ))
          .with('rejected', () => (
            <>
              <CloseOutlineIcon intent="negative" />
              <Text intent="negative" overflow="nowrap">
                {t('evaluationFail')}
              </Text>
            </>
          ))
          .with('no_decision', () =>
            requirement.status === 'reviewing' ? (
              <>
                <HourglassHalfOutlineIcon />
                <Text overflow="nowrap">{t('evaluationInReview')}</Text>
              </>
            ) : (
              <>
                <ClockOutlineIcon />
                <Text overflow="nowrap">{t('evaluationNotSubmittedYet')}</Text>
              </>
            ),
          )
          .exhaustive()}
      </Flex>
    </Table.Cell>
  );
}

const { Form, Field } = createForm<{ reason: string; reasonAdditionalNote: string }>();

function EvaluationSelect({
  evaluation,
  answerKey,
  item,
  onPendingEvaluationChange,
  requirementTemplateNumber,
}: Extract<TableData, { answerKey: string }> & {
  onPendingEvaluationChange?: (isPending: boolean) => void;
  requirementTemplateNumber: ApplicationRequirement['requirementTemplateNumber'];
}) {
  const { t } = useTranslation('RequirementEvaluationTable');
  const toasts = useToasts();
  const [open, setOpen] = useState(false);
  const [tentativeEvaluation, setTentativeEvaluation] = useState<typeof evaluation | null>(null);
  const { trackEvent } = useRequirementListEventTracker();

  const { submitEvaluation, getPendingSubmission, showInvalidState } = useContextOrThrow(
    RequirementEvaluationTableContext,
  );

  const pendingSubmission = getPendingSubmission(answerKey, item);
  const loading = pendingSubmission && pendingSubmission?.status !== evaluation;
  const disabled = Boolean(pendingSubmission);
  const isInvalid = !open && showInvalidState && evaluation === 'no_decision';

  return (
    <Table.Cell>
      <Popover
        open={open}
        onOpenChange={newOpen => {
          if (!newOpen) {
            setTentativeEvaluation(null);
            trackEvent({
              event: 'Review - Evaluation - Dismissed add message to applicant',
            });
          }
          setOpen(newOpen);
        }}
        placement="bottom-end"
      >
        <div style={{ width: '174px' }}>
          <Popover.Anchor>
            <Select
              aria-label={t('evaluationSelectAriaLabel')}
              placeholder=""
              appearance="styled"
              value={tentativeEvaluation || pendingSubmission?.status || evaluation}
              onChange={newTentativeEvaluation => {
                assert(
                  newTentativeEvaluation === 'approved' ||
                    newTentativeEvaluation === 'rejected' ||
                    newTentativeEvaluation === 'no_decision',
                );

                if (newTentativeEvaluation === 'approved') {
                  onPendingEvaluationChange?.(true);
                  submitEvaluation({
                    answerKey,
                    item,
                    status: 'approved',
                    onError: toasts.negative,
                    onSuccess: () => onPendingEvaluationChange?.(false),
                  });
                  trackEvent({
                    event: 'Review - Evaluation - Clicked pass',
                    data: {
                      requirementTemplateReferenceNumber: requirementTemplateNumber,
                    },
                  });
                }

                if (newTentativeEvaluation === 'rejected') {
                  setTentativeEvaluation('rejected');
                  trackEvent({
                    event: 'Review - Evaluation - Clicked fail',
                    data: {
                      requirementTemplateReferenceNumber: requirementTemplateNumber,
                    },
                  });
                }
              }}
              disabled={disabled}
              loading={loading}
              intent={isInvalid ? 'negative' : 'secondary'}
              helpText={isInvalid && !loading ? t('requiredValue') : undefined}
            >
              {evaluation === 'no_decision' && <Select.Option value="no_decision" label="Select" />}
              <Select.Option
                value="approved"
                label={t('evaluationPass')}
                icon={CheckMarkOutlineIcon}
                intent="positive"
              />
              <Popover.Trigger enabled={evaluation !== 'rejected'}>
                <Select.Option
                  value="rejected"
                  label={t('evaluationFail')}
                  icon={CloseOutlineIcon}
                  intent="negative"
                />
              </Popover.Trigger>
            </Select>
          </Popover.Anchor>
        </div>
        <Popover.Content width="md">
          <Form
            defaultValues={{ reason: '', reasonAdditionalNote: '' }}
            onSubmit={values => {
              setTentativeEvaluation(null);
              onPendingEvaluationChange?.(true);
              submitEvaluation({
                status: 'rejected',
                answerKey,
                item,
                ...values,
                onError: toasts.negative,
                onSuccess: () => {
                  onPendingEvaluationChange?.(false);
                },
              });
              trackEvent({
                event: 'Review - Evaluation - Added fail category and message to applicant',
                data: {
                  requirementTemplateReferenceNumber: requirementTemplateNumber,
                  failCategory: values.reason,
                },
              });

              setOpen(false);
            }}
          >
            <Popover.Heading>{t('evaluationPopover.heading')}</Popover.Heading>
            <Flex direction="column" gap={4}>
              <Popover.Description>
                <Text>{t('evaluationPopover.description')}</Text>
              </Popover.Description>
              <EvaluationFormFields />
            </Flex>
            <Popover.Actions>
              <Button
                emphasis="highlighted"
                width="fill"
                size="sm"
                onClick={() => {
                  setTentativeEvaluation(null);
                  trackEvent({
                    event: 'Review - Evaluation - Dismissed add message to applicant',
                  });
                  setOpen(false);
                }}
              >
                {t('evaluationPopover.cancelButton')}
              </Button>
              <Button intent="primary" width="fill" type="submit" size="sm">
                {t('evaluationPopover.confirmButton')}
              </Button>
            </Popover.Actions>
          </Form>
        </Popover.Content>
      </Popover>
    </Table.Cell>
  );
}

function useSubmitEvaluation(requirement: ApplicationRequirement) {
  const { setNextUpdate, checkHasMoreUpdates, updateStatus } = useUpdateApplicationRequirement();
  const [pendingSubmissions, setPendingSubmissions] = useState<Record<string, Decision[]> | null>(
    null,
  );

  function submitEvaluation({
    answerKey,
    item,
    status,
    reason,
    reasonAdditionalNote,
    onError,
    onSuccess,
  }: {
    answerKey: string;
    item?: string;
    status: Decision['status'];
    reason?: string;
    reasonAdditionalNote?: string;
    onError: (err: Error) => void;
    onSuccess: () => void;
  }) {
    const decision: Decision = item
      ? {
          type: 'per_item',
          item,
          status,
          reason,
          reasonAdditionalNote,
        }
      : {
          type: 'overall',
          status,
          reason,
          reasonAdditionalNote,
        };

    setPendingSubmissions(prevPendingSubmissions => ({
      ...prevPendingSubmissions,
      [answerKey]: upsert(
        prevPendingSubmissions?.[answerKey],
        d =>
          decision.type === 'overall'
            ? d.type === 'overall'
            : d.type === 'per_item' && d.item === decision.item,
        decision,
      ),
    }));

    setNextUpdate(
      prevUpdates => {
        const prevAnswers =
          prevUpdates?.answers || requirement.responses?.answersHistory?.[0].answers || [];
        return {
          ...prevUpdates,
          id: requirement.id,
          type: requirement.type,
          answers: prevAnswers.map(prevAnswer => {
            if (prevAnswer.key === answerKey) {
              return {
                ...prevAnswer,
                decisions: upsert(
                  prevAnswer.decisions,
                  d =>
                    decision.type === 'overall'
                      ? d.type === 'overall'
                      : d.type === 'per_item' && d.item === decision.item,
                  decision,
                ),
              };
            }
            return prevAnswer;
          }),
        };
      },
      {
        onError: err => {
          setPendingSubmissions(null);
          onError(err);
        },
        onSuccess: () => {
          if (!checkHasMoreUpdates()) {
            setPendingSubmissions(null);
          }
          onSuccess();
        },
      },
    );
  }

  return {
    submissionStatus: updateStatus,
    submitEvaluation,
    getPendingSubmission: (answerKey: string, item?: string) => {
      if (item) {
        return pendingSubmissions?.[answerKey]?.find(d => d.type === 'per_item' && d.item === item);
      }

      return pendingSubmissions?.[answerKey]?.find(d => d.type === 'overall');
    },
  };
}

function EvaluationFormFields() {
  const { t } = useTranslation('RequirementEvaluationTable');
  const { evaluationMetadata } = useEvaluationMetadata();

  return (
    <>
      <Field
        as={Select}
        name="reason"
        appearance="styled"
        label={t('evaluationForm.reasonLabel')}
        required={t('evaluationForm.reasonRequiredMessage')}
        helpText={t('evaluationForm.reasonHelpText')}
        enforceNoMissingOption
      >
        {evaluationMetadata.rejectionOptions.map(option => (
          <Select.Option key={option.code} label={option.displayName} value={option.code} />
        ))}
      </Field>
      <Field
        as={Textarea}
        name="reasonAdditionalNote"
        label={t('evaluationForm.reasonAdditionalNoteLabel')}
        required={t('evaluationForm.reasonAdditionalNoteRequiredMessage')}
      />
    </>
  );
}

function AttachmentTags({
  questionDefinition,
}: {
  questionDefinition: Extract<FormDefinition['components'][number], { type: 'file' }>;
}) {
  const { t } = useTranslation('RequirementEvaluationTable');
  const applyproofCompatible = questionDefinition?.properties?.applyproofCompatible;
  const verifiedByApplyproof = questionDefinition?.properties?.verifiedByApplyproof;
  const requiresNotarization = questionDefinition?.properties?.requiresNotarization;
  if (!applyproofCompatible && !requiresNotarization) {
    return null;
  }

  return (
    <Flex direction={{ xs: 'column', sm: 'row' }} gap={2}>
      {!verifiedByApplyproof && applyproofCompatible && (
        <Tag intent="secondary">{t('applyproofCompatible')}</Tag>
      )}
      {verifiedByApplyproof && <Tag intent="secondary">{t('applyproofVerified')}</Tag>}
      {requiresNotarization && <Tag intent="secondary">{t('notarizationRequired')}</Tag>}
    </Flex>
  );
}
